import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";

import container from "@src/container";
import {
  Link,
  Loader,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import {
  Column,
  ColumnOptions,
  ColumnType,
  Table,
  UserCell,
} from "@transfr-inc/dashboard-components/data-table";

import { ColumnHeaderCourseTooltip } from "@components/custom/content";
import { ExemptUserModal } from "@components/modals";
import { CourseCell } from "@components/table-cells";
import { useNotificationContext } from "@contexts/notification-context";
import { createUserDetailLink } from "@lib/utils/navigation.utils";
import { getContentTypeById } from "@transfr-inc/dashboard-components/curriculum";

export const CourseGradebookTable = ({
  data,
  activities,
  classroom,
  maxVisibleActivities = 8,
  onSortClick,
  isLoading,
  forceNoUnitHierarchy = false,
  hasUnits = false,
  readOnly,
  dataTableKey,
}) => {
  const [gradebookColumns, setGradebookColumns] = useState([]);
  const [tableData, setTableData] = useState();
  const [exemptUserModalOpen, setExemptUserModalOpen] = useState(false);
  const [exemptUserData, setExemptUserData] = useState();

  const { insightsService } = container;
  const { addNotification } = useNotificationContext();

  const getGradebookColumns = useCallback(
    (activities, forceNoUnitHierarchy, hasUnits) => {
      const hasUnitHierarchy = !forceNoUnitHierarchy && hasUnits;

      return activities.map((activity) => {
        const headerGroups = [];

        if (hasUnitHierarchy) {
          // Unit->Lesson->Activities hierarchy
          headerGroups.push(
            {
              id: activity.unitId,
              title: `Unit ${activity.unitOrderNumber}: ${activity.unitTitle}`,
              order: activity.unitOrderNumber,
              level: 1,
            },
            {
              id: `${activity.unitId}-${activity.lessonId}`,
              title: `Lesson ${activity.lessonOrderNumber}: ${activity.lessonTitle}`,
              order: activity.lessonOrderNumber,
              level: 2,
            }
          );
        } else {
          // Lesson->Activities hierarchy (no units)
          headerGroups.push({
            id: activity.lessonId,
            title: `Lesson ${activity.lessonOrderNumber}: ${activity.lessonTitle}`,
            order: activity.lessonOrderNumber,
            level: 1,
          });
        }

        const column = new Column(
          activity.activityId,
          (
            <ColumnHeaderCourseTooltip
              tooltipText={activity.title}
              type={activity.contentType}
              mimeType={activity.mimeType}
              headerTitle={activity.title}
            ></ColumnHeaderCourseTooltip>
          ),
          ColumnType.CUSTOM,
          new ColumnOptions({
            customFunction: (value) => {
              return <CourseCell key={activity?.id} value={value} />;
            },
            sortingEnabled: false,
            headerGroups,
          })
        );
        return column;
      });
    },
    [dataTableKey]
  );

  const UsersColumn = useMemo(
    () =>
      new Column(
        "lastName",
        "Name",
        ColumnType.CUSTOM,
        new ColumnOptions({
          sortingEnabled: true,
          isFixed: true,
          width: 250,
          sortKeyFields: ["isAnonymous", "lastName"],
          customFunction: (_, row) => {
            return (
              <UserCell
                lastName={row.lastName}
                firstName={row.firstName}
                isAnonymous={row.isAnonymous}
                userDetailRoute={
                  <Link
                    routerComponent={NavLink}
                    to={createUserDetailLink({
                      userId: row.userId,
                      search: {
                        classroomId: classroom.classroomId,
                        from: classroom.name,
                      },
                    })}
                  >{`${row.lastName}, ${row.firstName}`}</Link>
                }
              />
            );
          },
        })
      ),
    [classroom]
  );

  useEffect(() => {
    if (data && activities) {
      const cols = getGradebookColumns(
        activities,
        forceNoUnitHierarchy,
        hasUnits
      );
      const columns = [UsersColumn, ...cols];
      setGradebookColumns(columns);
    }
  }, [data, activities, UsersColumn, forceNoUnitHierarchy, hasUnits]);

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const handleCellClick = (data) => {
    if (!data?.id || !data?.userId) return;
    setExemptUserData(data);
    setExemptUserModalOpen(true);
  };

  const updateExemptUser = async (data) => {
    const {
      userId,
      activityId,
      exempt,
      contentType,
      exemptReason,
      otherReason,
      unitId,
      learningExperienceId,
      simulationCode,
      lessonId,
    } = data;

    const contentTypeText = getContentTypeById(contentType).key;
    try {
      const reason = otherReason ? otherReason : exemptReason;
      await insightsService.updateUserActivityMetrics(
        classroom.courseId,
        classroom.classroomId,
        userId,
        {
          activityId,
          learningExperienceId,
          simulationCode,
          exempt,
          exemptReason: reason,
          contentType: contentTypeText,
          lessonId,
          unitId,
        }
      );

      setTableData((prev) =>
        prev?.map((row) => {
          if (row.userId !== userId || !row[activityId]) return row;
          return {
            ...row,
            [activityId]: { ...row[activityId], exempt, exemptReason },
          };
        })
      );
    } catch (error) {
      console.error("Error exempting user:", error);
      addNotification({
        type: NotificationType.error,
        message: "Error exempting user. Please try again.",
        closable: true,
      });
    } finally {
      setExemptUserModalOpen();
    }
  };

  return (
    <>
      <ExemptUserModal
        open={exemptUserModalOpen}
        onClose={() => setExemptUserModalOpen()}
        onApply={updateExemptUser}
        initialData={exemptUserData}
        allowExempt
      />
      {isLoading && <Loader fixed />}
      <Table
        key={dataTableKey}
        className="gradebook-table"
        columns={gradebookColumns}
        data={tableData}
        maxVisibleColumns={maxVisibleActivities}
        onSortClick={onSortClick}
        staticHeaders
        keyValue="userId"
        minVisibleRows={10}
        handleCellClick={readOnly ? undefined : handleCellClick}
      />
    </>
  );
};
